import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatCurrency, formatDate, daysUntil } from "../lib/api";
import { MODULE_SCHEMAS } from "../lib/moduleSchemas";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import {
  ArrowUpRight, TrendingUp, TrendingDown, Wallet, ShieldCheck,
  AlertTriangle, CalendarClock, FileWarning, Clock, Sparkles,
  CalendarCheck2,
} from "lucide-react";
import { toast } from "sonner";

function Stat({ label, value, sub, icon: Icon, tone = "default", testid }) {
  const toneClass = {
    default: "bg-white",
    dark: "bg-slate-900 text-white",
    success: "bg-emerald-50 border-emerald-200",
    warn: "bg-amber-50 border-amber-200",
  }[tone];
  return (
    <Card className={`p-5 ${toneClass} transition-all duration-200 hover:-translate-y-[1px] hover:shadow-md`}
      data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-overline" style={tone === "dark" ? { color: "#cbd5e1" } : {}}>{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${tone === "dark" ? "text-slate-300" : "text-slate-400"}`} strokeWidth={1.75} />}
      </div>
      <div className="stat-value mt-3">{value}</div>
      {sub && <div className={`text-xs mt-2 ${tone === "dark" ? "text-slate-300" : "text-slate-500"}`}>{sub}</div>}
    </Card>
  );
}

export default function Dashboard({ onData }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
      onData?.(data.counts_by_module || {});
    } catch (e) {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line
  }, []);

  const seedDemo = async () => {
    await api.post("/demo/seed");
    toast.success("Demo data loaded");
    await load();
  };

  if (loading || !data) {
    return <div className="py-20 text-center text-slate-500 text-sm">Loading your vault...</div>;
  }

  // Compute review status
  const lastReview = data.last_review_at;
  const reviewDays = lastReview
    ? Math.floor((Date.now() - new Date(lastReview).getTime()) / 86400000)
    : null;
  const reviewDue = reviewDays === null || reviewDays > 330;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Hero */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-overline">Your Vault</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">
            Everything your family would need —<br className="hidden sm:block" /> organised, safe, findable.
          </h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDemo} data-testid="dashboard-seed-demo-button">
            <Sparkles className="h-4 w-4 mr-2" /> Load demo data
          </Button>
          <Button asChild data-testid="dashboard-emergency-plan-button">
            <Link to="/emergency-plan">Emergency Plan →</Link>
          </Button>
        </div>
      </div>

      {/* Yearly Review prompt */}
      {reviewDue ? (
        <Card className="p-6 bg-gradient-to-br from-sky-50 to-white border-sky-200" data-testid="dashboard-review-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-4 min-w-0">
              <div className="h-10 w-10 rounded-md bg-sky-100 text-sky-700 flex items-center justify-center shrink-0">
                <CalendarCheck2 className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <div>
                <div className="label-overline text-sky-700">Annual Health Check</div>
                <div className="font-heading text-xl font-semibold mt-1">
                  {lastReview ? "Time for your yearly review" : "Run your first yearly review"}
                </div>
                <p className="text-sm text-slate-600 mt-1 max-w-xl">
                  5-minute guided flow: fix nominees, confirm expiring documents, refresh
                  stale entries. Bumps your Emergency Readiness.
                  {lastReview && (
                    <span className="text-xs text-slate-500 block mt-1">
                      Last review: {reviewDays} days ago
                    </span>
                  )}
                </p>
              </div>
            </div>
            <Button asChild data-testid="dashboard-start-review-button">
              <Link to="/review">
                Start review <ArrowUpRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </div>
        </Card>
      ) : (
        <Card className="p-4 bg-emerald-50 border-emerald-200 flex items-center justify-between gap-3 flex-wrap" data-testid="dashboard-review-ok-card">
          <div className="flex items-center gap-3 text-sm">
            <CalendarCheck2 className="h-4 w-4 text-emerald-700" />
            <span className="text-emerald-900">
              Yearly review completed {reviewDays} days ago. Next due in {365 - reviewDays} days.
            </span>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/review">Run again</Link>
          </Button>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          label="Net Worth Snapshot"
          value={formatCurrency(data.net_worth, true)}
          sub={`${formatCurrency(data.total_assets, true)} assets · ${formatCurrency(data.total_liabilities, true)} liab.`}
          icon={Wallet} tone="dark"
          testid="stat-net-worth"
        />
        <Stat
          label="Total Assets"
          value={formatCurrency(data.total_assets, true)}
          sub="Investments + Property"
          icon={TrendingUp}
          testid="stat-total-assets"
        />
        <Stat
          label="Total Liabilities"
          value={formatCurrency(data.total_liabilities, true)}
          sub="Outstanding across loans"
          icon={TrendingDown}
          testid="stat-total-liabilities"
        />
        <Stat
          label="Emergency Readiness"
          value={`${data.readiness_score}%`}
          sub="Nominees, will, plan, contacts"
          icon={ShieldCheck}
          tone={data.readiness_score >= 70 ? "success" : "warn"}
          testid="stat-readiness"
        />
      </div>

      {/* Progress & First steps */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-2" data-testid="dashboard-progress-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-overline">Profile Completion</div>
              <div className="font-heading text-2xl font-semibold tracking-tight mt-1">
                {data.profile_completion}% complete
              </div>
            </div>
            <Badge variant="outline" className="font-medium">
              {Object.values(data.counts_by_module).reduce((a, b) => a + b, 0)} entries
            </Badge>
          </div>
          <Progress value={data.profile_completion} className="h-2" />
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {data.module_catalog.slice(0, 8).map((m) => {
              const c = data.counts_by_module[m.slug] || 0;
              return (
                <Link
                  key={m.slug}
                  to={`/m/${m.slug}`}
                  className="text-left p-3 rounded-md border border-slate-200 hover:border-slate-400 hover:bg-slate-50 transition-colors"
                  data-testid={`dashboard-module-tile-${m.slug}`}
                >
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">{m.name}</div>
                  <div className="font-heading text-xl font-semibold mt-1 tabular-nums">{c}</div>
                </Link>
              );
            })}
          </div>
        </Card>

        <Card className="p-6 bg-slate-900 text-white" data-testid="dashboard-first-steps-card">
          <div className="label-overline text-slate-300">What my family should do first</div>
          <div className="font-heading text-xl font-semibold mt-2 leading-tight">
            Three things to set up today
          </div>
          <ul className="mt-6 space-y-4 text-sm">
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-white text-slate-900 flex items-center justify-center text-xs font-semibold shrink-0">1</span>
              <div>
                <Link to="/emergency-access" className="font-medium hover:text-sky-300">Nominate emergency contacts</Link>
                <div className="text-slate-400 text-xs">Decide who can request access if something happens.</div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-white text-slate-900 flex items-center justify-center text-xs font-semibold shrink-0">2</span>
              <div>
                <Link to="/nominees" className="font-medium hover:text-sky-300">Fix missing nominees</Link>
                <div className="text-slate-400 text-xs">
                  {data.missing_nominees.length} accounts currently have no nominee.
                </div>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="h-6 w-6 rounded-full bg-white text-slate-900 flex items-center justify-center text-xs font-semibold shrink-0">3</span>
              <div>
                <Link to="/emergency-plan" className="font-medium hover:text-sky-300">Write your 24-hour checklist</Link>
                <div className="text-slate-400 text-xs">Tell your family where to start, who to call, what to freeze.</div>
              </div>
            </li>
          </ul>
          <Button asChild variant="secondary" className="w-full mt-6"
            data-testid="dashboard-first-steps-go-button">
            <Link to="/emergency-plan">Open Emergency Plan <ArrowUpRight className="h-4 w-4 ml-1" /></Link>
          </Button>
        </Card>
      </div>

      {/* Three column alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6" data-testid="dashboard-due-card">
          <div className="flex items-center gap-2 mb-3">
            <CalendarClock className="h-4 w-4 text-slate-700" strokeWidth={1.75} />
            <div className="font-heading font-semibold">Upcoming Due (60 days)</div>
          </div>
          {data.upcoming_due.length === 0 ? (
            <div className="text-sm text-slate-500">No upcoming premiums or EMIs.</div>
          ) : (
            <ul className="space-y-3">
              {data.upcoming_due.map((u) => {
                const d = daysUntil(u.due_date);
                return (
                  <li key={u.entity_id} className="flex items-start justify-between gap-3 text-sm">
                    <div className="min-w-0">
                      <Link to={`/m/${u.module}`} className="font-medium text-slate-900 hover:underline truncate block">
                        {u.title}
                      </Link>
                      <div className="text-xs text-slate-500">{MODULE_SCHEMAS[u.module]?.label}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-xs tabular-nums">{formatDate(u.due_date)}</div>
                      <Badge variant={d <= 7 ? "destructive" : "secondary"} className="mt-1 text-[10px]">
                        {d <= 0 ? "Due today" : `${d} days`}
                      </Badge>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card className="p-6" data-testid="dashboard-missing-nominees-card">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={1.75} />
            <div className="font-heading font-semibold">Missing Nominees</div>
          </div>
          {data.missing_nominees.length === 0 ? (
            <div className="text-sm text-slate-500">All tracked accounts have nominees. ✓</div>
          ) : (
            <ul className="space-y-2">
              {data.missing_nominees.slice(0, 6).map((m) => (
                <li key={m.entity_id} className="text-sm flex items-center justify-between gap-2">
                  <Link to={`/m/${m.module}`} className="hover:underline truncate">{m.title}</Link>
                  <span className="text-xs text-slate-500">{MODULE_SCHEMAS[m.module]?.label}</span>
                </li>
              ))}
            </ul>
          )}
          <Button asChild variant="outline" size="sm" className="mt-4"
            data-testid="dashboard-open-nominees-button">
            <Link to="/nominees">Open Nominee Tracker</Link>
          </Button>
        </Card>

        <Card className="p-6" data-testid="dashboard-expiring-docs-card">
          <div className="flex items-center gap-2 mb-3">
            <FileWarning className="h-4 w-4 text-slate-700" strokeWidth={1.75} />
            <div className="font-heading font-semibold">Expiring Documents</div>
          </div>
          {data.expiring_documents.length === 0 ? (
            <div className="text-sm text-slate-500">No documents expiring soon.</div>
          ) : (
            <ul className="space-y-2">
              {data.expiring_documents.map((d) => (
                <li key={d.entity_id} className="text-sm flex items-center justify-between gap-2">
                  <Link to="/m/documents" className="hover:underline truncate">{d.title}</Link>
                  <span className="text-xs tabular-nums text-slate-500">{formatDate(d.expiry_date)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Recently updated */}
      <Card className="p-6" data-testid="dashboard-recent-card">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-4 w-4 text-slate-700" strokeWidth={1.75} />
          <div className="font-heading font-semibold">Recently Updated</div>
        </div>
        {data.recently_updated.length === 0 ? (
          <div className="text-sm text-slate-500">Nothing yet — start by adding your first bank account.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.recently_updated.map((r) => (
              <li key={r.id} className="py-2.5 flex items-center justify-between gap-3 text-sm">
                <Link to={`/m/${r.module}`} className="font-medium hover:underline truncate">{r.title}</Link>
                <div className="flex items-center gap-3 shrink-0">
                  <Badge variant="outline" className="text-[10px]">
                    {MODULE_SCHEMAS[r.module]?.label || r.module}
                  </Badge>
                  <span className="text-xs text-slate-500">{formatDate(r.updated_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
